// Pure, total order-presentation selector.
//
// `presentationFor` maps every `OrderStatus` value — paired with any EFM filing
// status, or none — to exactly one `SubmissionPresentation`. It is the single
// source of truth for which submission banner an order detail view renders; no
// UI is wired here, downstream UI issues build on this selector.
//
// Pure function — no side effects, no I/O, no throws. Determined solely by the
// order's status and its EFM filing status (never by navigation / query-string
// state), so the same inputs always yield the same presentation.
/**
 * The seven EFM filing statuses, mirroring `efmFilingStatusEnum` in
 * `@lds-efsp/database` (`packages/database/src/schema/efm-filings.ts`) verbatim.
 * Defined locally because shared-contracts MUST NOT import `@lds-efsp/database`
 * — database depends on shared-contracts, not the reverse. The runtime tuple
 * gives the invariant test a single iteration source while keeping the mirror
 * explicit. Note `under_review` / `error` carry the EFM enum's underscores, and
 * `partially_accepted` (underscore) is distinct from the order-schema
 * `HoldReason` value `partially-accepted` (hyphen) — do not conflate them.
 */
export const EFM_FILING_STATUSES = [
    'pending',
    'submitted',
    'under_review',
    'accepted',
    'partially_accepted',
    'rejected',
    'error',
];
/**
 * Map an order's status and (optional) EFM filing status to its submission
 * presentation. Precedence: the four order-priority states (`accepted`,
 * `partially-accepted`, `submission-in-doubt`, `escalated`) return a fixed
 * presentation regardless of EFM status; every other status applies the EFM
 * rejected / partial override before its own default.
 *
 * The `switch (orderStatus)` is exhaustive over the 12 `OrderStatus` members; a
 * future member added without a case fails the `never` exhaustiveness check at
 * compile time.
 */
export function presentationFor(orderStatus, efmStatus) {
    switch (orderStatus) {
        // Order-priority states — fixed presentation, ignoring EFM filing status.
        case 'accepted':
            return 'SUCCESS';
        case 'partially-accepted':
            return 'PARTIALLY_ACCEPTED';
        case 'submission-in-doubt':
            return 'PENDING_CONFIRMATION';
        case 'escalated':
            return 'NEEDS_ATTENTION';
        // A rejected order is REJECTED unless the court returned a partial accept.
        case 'rejected':
            return efmStatus === 'partially_accepted' ? 'PARTIALLY_ACCEPTED' : 'REJECTED';
        // Submitted / awaiting court review — EFM rejection or partial takes precedence.
        case 'submitted':
        case 'pending-review':
            if (efmStatus === 'rejected')
                return 'REJECTED';
            if (efmStatus === 'partially_accepted')
                return 'PARTIALLY_ACCEPTED';
            return 'AWAITING_REVIEW';
        // Pre-submit / inert lifecycle states — no banner unless the court rejected
        // or partially accepted.
        case 'draft':
        case 'ready-for-review':
        case 'in-progress':
        case 'completed':
        case 'cancelled':
            if (efmStatus === 'rejected')
                return 'REJECTED';
            if (efmStatus === 'partially_accepted')
                return 'PARTIALLY_ACCEPTED';
            return 'NONE';
        default: {
            // Exhaustiveness guard: adding an OrderStatus member without a case above
            // makes this assignment a compile error.
            const _exhaustive = orderStatus;
            return _exhaustive;
        }
    }
}
